import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  MUNILY_CHECKBOX_LIST_DEFAULTS,
  MunilyCheckboxListCheckboxType,
  MunilyCheckboxListColumnsSizing,
  MunilyCheckboxListLayout,
  MunilyCheckboxListOption,
  MunilyCheckboxListSelectionMode,
} from './checkbox-list.types';

@Component({
  selector: 'common-checkbox-list',
  templateUrl: './checkbox-list.component.html',
  styleUrls: ['./checkbox-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxListComponent<TValue extends string = string> implements OnChanges {
  @Input() titleKey: string = MUNILY_CHECKBOX_LIST_DEFAULTS.titleKey;
  @Input() options: Array<MunilyCheckboxListOption<TValue>> = MUNILY_CHECKBOX_LIST_DEFAULTS.options as Array<
    MunilyCheckboxListOption<TValue>
  >;

  /** Default: bullet. */
  @Input() checkboxType: MunilyCheckboxListCheckboxType = MUNILY_CHECKBOX_LIST_DEFAULTS.checkboxType;
  /** Default: single selection. */
  @Input() selectionMode: MunilyCheckboxListSelectionMode = MUNILY_CHECKBOX_LIST_DEFAULTS.selectionMode;
  /** Default: rows. */
  @Input() layout: MunilyCheckboxListLayout = MUNILY_CHECKBOX_LIST_DEFAULTS.layout;
  /** Only used when layout === 'columns'. */
  @Input() columns: number = MUNILY_CHECKBOX_LIST_DEFAULTS.columns;
  /** Only used when layout === 'columns'. Default: fill (stretch). */
  @Input() columnsSizing: MunilyCheckboxListColumnsSizing = MUNILY_CHECKBOX_LIST_DEFAULTS.columnsSizing;

  @Input() disabled: boolean = MUNILY_CHECKBOX_LIST_DEFAULTS.disabled;

  /** Single-selection value (when selectionMode === 'single'). */
  @Input() value: TValue | null = null;
  /** Multi-selection values (when selectionMode === 'multiple'). */
  @Input() values: TValue[] = [];

  @Output() selectionChange = new EventEmitter<TValue | TValue[] | null>();

  public hostClasses: string[] = [];
  public optionsClasses: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] || changes['layout'] || changes['columnsSizing']) {
      this.syncClasses();
    }
  }

  constructor() {
    this.syncClasses();
  }

  private syncClasses(): void {
    this.hostClasses = this.disabled ? ['common-checkbox-list--disabled'] : [];

    const options: string[] = [];
    if (this.layout === 'rows') options.push('common-checkbox-list__options--rows');
    if (this.layout === 'columns') {
      options.push('common-checkbox-list__options--columns');
      if (this.columnsSizing === 'content') {
        options.push('common-checkbox-list__options--columns-content');
      }
    }
    this.optionsClasses = options;
  }

  get isColumnsLayout(): boolean {
    return this.layout === 'columns';
  }

  get columnsCount(): number {
    const safe = Number(this.columns);
    if (!Number.isFinite(safe)) return 2;
    return Math.max(1, Math.min(4, Math.floor(safe)));
  }

  isChecked(optionValue: TValue): boolean {
    if (this.selectionMode === 'multiple') {
      return this.values.includes(optionValue);
    }
    return this.value === optionValue;
  }

  onOptionToggle(optionValue: TValue, checked: boolean): void {
    if (this.disabled) return;

    if (this.selectionMode === 'multiple') {
      const set = new Set(this.values);
      if (checked) {
        set.add(optionValue);
      } else {
        set.delete(optionValue);
      }
      const next = Array.from(set.values());
      this.values = next;
      this.selectionChange.emit(next);
      return;
    }

    // single
    const next = checked ? optionValue : null;
    this.value = next;
    this.selectionChange.emit(next);
  }
}

